import {
  BedDouble,
  Users,
  Package,
  Wrench,
  Truck,
  ClipboardList,
  Stethoscope,
  FlaskConical,
  CalendarDays,
  Activity,
} from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import {
  HeaderKpi,
  SectionCard,
  RowItem,
  CenterHeader,
} from "./center/CenterPrimitives";

type Tone = "success" | "warning" | "danger";

const toneColor = (tone: Tone) =>
  tone === "danger"
    ? "oklch(0.7 0.2 35)"
    : tone === "warning"
      ? "oklch(0.82 0.16 85)"
      : "oklch(0.78 0.18 150)";

const toneText = (tone: Tone) =>
  tone === "danger"
    ? "text-accent"
    : tone === "warning"
      ? "text-[oklch(0.85_0.15_85)]"
      : "text-success";

function toneFromPct(pct: number): Tone {
  if (pct >= 90) return "danger";
  if (pct >= 75) return "warning";
  return "success";
}

function Donut({
  value,
  total,
  label,
  sub,
  tone,
  size = 96,
}: {
  value: number;
  total: number;
  label: string;
  sub?: string;
  tone?: Tone;
  size?: number;
}) {
  const pct = Math.round((value / total) * 100);
  const t = tone ?? toneFromPct(pct);
  const color = toneColor(t);
  const data = [
    { name: "v", value: pct },
    { name: "r", value: Math.max(0, 100 - pct) },
  ];
  return (
    <div className="flex flex-col items-center gap-1.5">
      <div className="relative" style={{ width: size, height: size }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              innerRadius={size * 0.34}
              outerRadius={size * 0.46}
              startAngle={90}
              endAngle={-270}
              stroke="none"
              isAnimationActive={false}
            >
              <Cell fill={color} />
              <Cell fill="oklch(1 0 0 / 0.06)" />
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div className="pointer-events-none absolute inset-0 grid place-items-center">
          <div className="text-center">
            <div className={`text-base font-light leading-none ${toneText(t)}`}>{pct}%</div>
            <div className="mt-0.5 text-[9px] text-muted-foreground">
              {value}/{total}
            </div>
          </div>
        </div>
      </div>
      <div className="text-center">
        <div className="text-[11px] font-medium leading-tight">{label}</div>
        {sub && <div className="text-[9px] text-muted-foreground">{sub}</div>}
      </div>
    </div>
  );
}

function EquipTile({
  label,
  value,
  total,
  status,
  tone,
}: {
  label: string;
  value: number;
  total: number;
  status: string;
  tone: Tone;
}) {
  return (
    <div className="flex flex-col items-center gap-1 rounded-xl bg-white/[0.02] p-3 ring-1 ring-white/5">
      <Donut value={value} total={total} label={label} sub={status} tone={tone} size={84} />
    </div>
  );
}

function InvDonut({
  name,
  value,
  max,
  tone,
}: {
  name: string;
  value: number;
  max: number;
  tone: Tone;
}) {
  return (
    <div className="flex flex-col items-center gap-1 rounded-xl bg-white/[0.02] p-3 ring-1 ring-white/5">
      <Donut value={value} total={max} label={name} tone={tone} size={84} />
    </div>
  );
}

export default function OperationsCenterView({ onBack }: { onBack: () => void }) {
  return (
    <div className="flex flex-col gap-5">
      <CenterHeader
        title="Operations Center"
        scope="Lits · Personnel · Stocks · Équipements · Logistique"
        onBack={onBack}
      />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <HeaderKpi icon={BedDouble} value="62/80" label="Lits occupés" delta="0%" tone="success" seed={1} />
        <HeaderKpi icon={Users} value="47/55" label="Staff en poste" delta="+2%" tone="success" seed={2} />
        <HeaderKpi icon={Package} value="3" label="Stocks critiques" delta="0%" tone="warning" seed={3} />
        <HeaderKpi icon={Wrench} value="1" label="Équipements en panne" delta="-1%" tone="danger" seed={4} />
      </div>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <SectionCard title="Lits par service" scope="62 / 80 occupés · 77%" icon={BedDouble} tone="success">
          <div className="grid grid-cols-3 gap-3 sm:grid-cols-6">
            <Donut value={12} total={12} label="Urgences" />
            <Donut value={18} total={24} label="Médecine" />
            <Donut value={14} total={18} label="Chirurgie" />
            <Donut value={7} total={8} label="Réa" />
            <Donut value={6} total={10} label="Maternité" />
            <Donut value={5} total={8} label="Pédiatrie" />
          </div>
        </SectionCard>

        <SectionCard title="Personnel en poste" scope="47 / 55 · shift jour" icon={Users} tone="success">
          <div className="grid grid-cols-3 gap-3 sm:grid-cols-6">
            <Donut value={12} total={14} label="Médecins" />
            <Donut value={18} total={22} label="Infirmiers" />
            <Donut value={8} total={10} label="Aides-soig." />
            <Donut value={3} total={3} label="Pharmaciens" />
            <Donut value={4} total={4} label="Tech. labo" />
            <Donut value={2} total={2} label="Radiologues" />
          </div>
          <div className="mt-3 text-[11px] text-muted-foreground">
            Prochain shift : 20:00 — 08:00 (équipe de nuit, 38 personnes)
          </div>
        </SectionCard>

        <SectionCard title="Équipements" scope="Disponibilité temps réel" icon={Wrench} tone="warning">
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
            <EquipTile label="Ambulances" value={2} total={3} status="Partiel" tone="warning" />
            <EquipTile label="Salles op." value={1} total={3} status="Partiel" tone="warning" />
            <EquipTile label="IRM" value={0} total={1} status="Indispo" tone="danger" />
            <EquipTile label="Scanner" value={1} total={2} status="OK" tone="success" />
            <EquipTile label="Échographes" value={2} total={3} status="OK" tone="success" />
          </div>
        </SectionCard>

        <SectionCard title="Inventaire" scope="Seuils min/max · alertes automatiques" icon={Package} tone="warning">
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-3">
            <InvDonut name="Gants nitrile" value={245} max={500} tone="success" />
            <InvDonut name="Masques FFP2" value={1280} max={2000} tone="success" />
            <InvDonut name="Seringues 10mL" value={89} max={1000} tone="warning" />
            <InvDonut name="Compresses" value={12} max={500} tone="danger" />
            <InvDonut name="Désinfectant" value={45} max={100} tone="success" />
            <InvDonut name="Cathéters IV" value={156} max={300} tone="success" />
          </div>
        </SectionCard>
      </div>

      <SectionCard title="Actions rapides" scope="Raccourcis opérationnels" icon={Activity} tone="neutral">
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <RowItem title="Stocks pharma" hint="Seuils · contrôles" />
          <RowItem title="Résultats labo" hint="TAT · critiques" />
          <RowItem title="Agenda" hint="Planifier RDV" />
          <RowItem title="Clinical OpC" hint="Vue médecin" />
        </div>
        <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
          <RowItem title="Logistique" hint="Brancardage · transferts" />
          <RowItem title="Maintenance" hint="Tickets équipements" />
          <RowItem title="Planning" hint="Shifts · gardes" />
          <RowItem title="Check-list" hint="Procédures qualité" />
        </div>
      </SectionCard>

      <div className="hidden">
        <Truck /> <ClipboardList /> <Stethoscope /> <FlaskConical /> <CalendarDays />
      </div>
    </div>
  );
}
